/**
 * BLRRD Selection — lasso.js  (Phase 4.B.select.2, Wave 8.2)
 *
 * Free-hand polygon rasterisation. The pointer-capture layer in the
 * legacy editor collects an array of `{x, y}` points during the
 * pointermove sequence (after the user pointerdowns on the lasso
 * overlay); on pointerup it auto-closes the polygon (last vertex back
 * to the first) and calls `rasterizeLasso` to fill the coverage mask.
 *
 * Algorithm — scanline odd-even fill + edge anti-alias band
 * ────────────────────────────────────────────────────────
 * For each scanline y between yMin and yMax:
 *
 *   1. Walk all polygon edges. For each edge that crosses y (one vertex
 *      on each side of the scanline), compute the x intercept by linear
 *      interpolation.
 *   2. Sort the intercepts left-to-right.
 *   3. Pair them up (0..1, 2..3, …) — the odd-even fill rule (Pineda
 *      1988 "A Parallel Algorithm for Polygon Rasterization"; this is
 *      also the SVG `fill-rule="evenodd"` and Sutherland-Hodgman 1974
 *      output convention).
 *   4. For each pair `[xL, xR]`, fill pixels x ∈ [floor(xL), ceil(xR))
 *      with coverage 1.0; clip the partial-cover pixels at xL and xR
 *      via 1-pixel analytic AA (½-pixel-rule — same form as
 *      `src/selection/rect.js`).
 *
 * Wu (1991) AA is unnecessary here because the scanline already gives
 * us exact x-intercepts; the partial coverage at the edge pixel is
 * `clamp(0, x+1 − xL, 1)` for the left edge and `clamp(0, xR − x, 1)`
 * for the right edge.
 *
 * For an analytic y-edge AA we'd need to evaluate coverage as a 2-D
 * polygon-pixel intersection, which is overkill for the lasso UX. The
 * x-axis AA alone gives visually clean edges on diagonal lasso strokes
 * because the human eye is far more sensitive to x-staircasing on
 * 60 Hz displays than y-staircasing (rolling-shutter myth aside).
 *
 * Polygon validation
 * ──────────────────
 * Points with NaN/Infinity coords are skipped. A polygon with < 3
 * vertices is a no-op (the rasteriser fills nothing). Self-intersecting
 * polygons fill per the odd-even rule (a figure-eight gets the
 * "bowtie" you'd expect — the centre is inside both lobes and so
 * cancels). This matches the SVG / canvas2D defaults.
 *
 * Provenance: clean-room from Pineda 1988 (edge function rasterisation),
 * Sutherland-Hodgman 1974 (polygon clipping conventions), and Heckbert
 * "Concepts of Pixel Coverage". NO GIMP/GEGL source consulted.
 */

/**
 * Rasterize a closed polygon into `mask` (Float32Array(w*h)) using
 * the odd-even fill rule with 1-pixel analytic AA on the x-axis edges.
 *
 * The caller is responsible for what's in `mask` on entry. The
 * rasteriser zeroes the mask, then writes coverage; combine semantics
 * (replace/add/subtract/intersect) are applied by the caller via
 * `src/selection/combine.js::combine()` against a scratch mask.
 *
 * Auto-closing: if `points[0] !== points[N-1]`, the polygon is treated
 * as closed by joining the last point back to the first. The pointer
 * capture flow in the legacy editor relies on this.
 *
 * @param {Float32Array} mask        destination, length = w*h
 * @param {number}        w
 * @param {number}        h
 * @param {Array<{x:number, y:number}>} points
 * @param {boolean}       [antiAlias=true]   1-px AA on the x edges
 * @returns {Float32Array}            same `mask` reference
 */
export function rasterizeLasso(mask, w, h, points, antiAlias = true) {
    if (!(mask instanceof Float32Array)) {
        throw new Error('rasterizeLasso: mask must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    if (mask.length !== W * H) {
        throw new Error(`rasterizeLasso: mask length mismatch (${mask.length} vs ${W * H})`);
    }
    mask.fill(0);

    if (!Array.isArray(points) || points.length < 3) return mask;

    /* Sanitise: drop NaN / Infinity vertices, copy into flat arrays to
     * avoid the property-access cost in the inner scanline loop. */
    const px = [], py = [];
    for (const p of points) {
        if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
        px.push(p.x);
        py.push(p.y);
    }
    const N = px.length;
    if (N < 3) return mask;

    /* Bounding box, clipped to canvas. */
    let yMin = Infinity, yMax = -Infinity;
    for (let i = 0; i < N; i++) {
        if (py[i] < yMin) yMin = py[i];
        if (py[i] > yMax) yMax = py[i];
    }
    const yLo = Math.max(0, Math.floor(yMin));
    const yHi = Math.min(H, Math.ceil(yMax) + 1);
    if (yHi <= yLo) return mask;

    /* Pre-allocate the intercept scratch — at most N intercepts per
     * sub-sample (one per polygon edge). */
    const xs = new Float64Array(N);

    /* For analytic 2-D coverage along both axes we sample the scanline
     * 4× vertically (at y + {0.125, 0.375, 0.625, 0.875}) and average.
     * Each sub-sample gets exact x-axis analytic coverage; the vertical
     * average gives O(N) sub-pixel coverage without quadratic cost.
     * antiAlias=false collapses to a single sample at y + 0.5. */
    const Y_OFFSETS = antiAlias ? [0.125, 0.375, 0.625, 0.875] : [0.5];
    const Y_WEIGHT  = 1 / Y_OFFSETS.length;

    for (let y = yLo; y < yHi; y++) {
        const rowBase = y * W;

        /* Accumulate coverage from each sub-sample. Per-pixel scratch
         * lives in a single Float32Array(W) re-used across rows; with
         * 4-tap sampling each pixel can sum to at most 4·Y_WEIGHT = 1. */
        let touchedXLo = W, touchedXHi = 0;
        const rowCov = _rowCov(W);
        rowCov.fill(0, 0, W);

        for (let s = 0; s < Y_OFFSETS.length; s++) {
            const ySample = y + Y_OFFSETS[s];

            let count = 0;
            for (let i = 0, j = N - 1; i < N; j = i++) {
                const yi = py[i], yj = py[j];
                /* Edge i—j crosses ySample iff one endpoint is below and
                 * the other ≥. Strict inequality on one side breaks the
                 * vertex-tie double-counting (the textbook "lower
                 * endpoint exclusive" rule from Pineda 1988). */
                if ((yi <= ySample && yj > ySample) || (yj <= ySample && yi > ySample)) {
                    const t = (ySample - yi) / (yj - yi);
                    xs[count++] = px[i] + t * (px[j] - px[i]);
                }
            }
            if (count < 2) continue;

            /* Sort intercepts ascending. */
            for (let i = 1; i < count; i++) {
                const v = xs[i];
                let k = i - 1;
                while (k >= 0 && xs[k] > v) { xs[k + 1] = xs[k]; k--; }
                xs[k + 1] = v;
            }

            /* Pair-fill, odd-even. */
            for (let p = 0; p + 1 < count; p += 2) {
                const xL = xs[p];
                const xR = xs[p + 1];
                if (xR <= 0 || xL >= W) continue;
                const xLClip = Math.max(0, xL);
                const xRClip = Math.min(W, xR);

                const ixL = Math.floor(xLClip);
                const ixR = Math.min(W, Math.ceil(xRClip));
                if (ixL < touchedXLo) touchedXLo = ixL;
                if (ixR > touchedXHi) touchedXHi = ixR;

                for (let x = ixL; x < ixR; x++) {
                    let cov = 1;
                    if (antiAlias) {
                        /* Same "pixel-as-unit-square coverage" model as
                         * src/selection/rect.js — interior pixels
                         * saturate at 1, boundary pixels carry partial
                         * coverage on x. */
                        const a = x > xL ? x : xL;
                        const b = (x + 1) < xR ? (x + 1) : xR;
                        cov = b - a;
                        if (cov < 0) cov = 0;
                        else if (cov > 1) cov = 1;
                    }
                    if (cov > 0) rowCov[x] += cov * Y_WEIGHT;
                }
            }
        }

        /* Commit the accumulated row coverage to the mask. */
        for (let x = touchedXLo; x < touchedXHi; x++) {
            const c = rowCov[x];
            if (c > 0) {
                /* Same scanline can be hit by multiple spans in self-
                 * intersecting polygons — `max` keeps the highest
                 * coverage rather than summing (which could exceed 1). */
                if (c > mask[rowBase + x]) mask[rowBase + x] = c > 1 ? 1 : c;
            }
        }
    }
    return mask;
}

/* Per-thread row-cov scratch — single allocation, grown on demand. We
 * keep it module-scoped so successive lasso commits don't re-alloc the
 * full W float row each call. */
let _rowCovBuf = null;
function _rowCov(W) {
    if (!_rowCovBuf || _rowCovBuf.length < W) _rowCovBuf = new Float32Array(W);
    return _rowCovBuf;
}

/**
 * Convenience helper: rasterize a single point (e.g. as a degenerate
 * lasso click) by drawing a 1-px square at floor(x), floor(y). Used by
 * the editor when the pointerdown/up sequence has no move events.
 *
 * @param {Float32Array} mask
 * @param {number}        w
 * @param {number}        h
 * @param {number}        x
 * @param {number}        y
 * @returns {Float32Array}
 */
export function rasterizePoint(mask, w, h, x, y) {
    if (!(mask instanceof Float32Array)) {
        throw new Error('rasterizePoint: mask must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    if (mask.length !== W * H) {
        throw new Error(`rasterizePoint: mask length mismatch`);
    }
    mask.fill(0);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return mask;
    const px = Math.floor(x), py = Math.floor(y);
    if (px < 0 || px >= W || py < 0 || py >= H) return mask;
    mask[py * W + px] = 1;
    return mask;
}
